import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Send, TrendingUp, Settings2, Wrench, ShieldCheck } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-4 py-3 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-2 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-400 uppercase block mb-2";
const FORM_STORE = "ndis_portal_form";

const TRACKS = {
  education: {
    label: "Option A: Foundational Skillset & Passion Track (Educational Only)",
    desc: "Perfect for participants on the Disability Support Pension (DSP) who want to learn advanced Image-to-3D AI asset workflows, game design, and digital creation without generating commercial income. Focuses purely on skill acquisition, digital inclusion, and building a lifelong passion.",
  },
  enterprise: {
    label: "Option B: Compliant Micro-Enterprise Track (Commercial with Plan Manager Cap)",
    desc: "Designed for participants who want to safely monetize their 3D asset packs online while staying strictly compliant with Centrelink DSP income thresholds. Includes an automated dashboard tool allowing Plan Managers or Nominees to set a monthly revenue cap.",
  },
};

const ACCESS_PROFILES = [
  ["lite_interface", "Neuro-Affirming Lite Interface", "Simplifies the Image-to-3D dashboard into a high-visibility, 3-button layout with minimal text and no distracting animations."],
  ["screen_reader", "Screen-Reader & Low-Vision Optimization", "Strict WCAG 2.1 AA compliance, high-contrast colour mode, increased scaling, and Text-to-Speech spatial descriptions for generated 3D models."],
  ["voice_control", "Voice-Prompting & Hands-Free Engine", "Microphone permissions inside the creator canvas — command the Image-to-3D engine entirely via vocal dictation."],
  ["self_paced", "Asynchronous Learning (Self-Paced Track)", "For psychosocial disability support. No live cohort times or strict deadlines — engagement milestones tracked dynamically for NDIS reporting."],
];

const DEFAULT_FORM = {
  participant_name: "", participant_age: "", ndis_number: "", contact_type: "participant", contact_name: "",
  email: "", phone: "", plan_type: "plan_managed", goal: "",
  track: "", revenue_cap_aud: 400, oversight_email: "",
  accessibility_profiles: [], hardware_addon: false, website: "",
};

function DspCalculator() {
  const [dspFn, setDspFn] = useState(1136);
  const [sales, setSales] = useState(12);
  const [price, setPrice] = useState(35);
  const dspAnnual = dspFn * 26;
  const creativeAnnual = sales * price * 12 * 0.8;
  const diff = Math.round(creativeAnnual - dspAnnual);
  const fmt = (n) => `$${Math.round(n).toLocaleString("en-AU")}`;

  return (
    <div data-testid="dsp-calculator" className="border border-white/10 bg-black/40 p-6 sm:p-8">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase mb-2 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Interactive Tool</p>
      <h2 className="font-sans font-bold text-2xl tracking-tight">DSP vs. Pro Developer: The Long-Term Maths</h2>
      <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-2xl">
        Drag the sliders to see the economic potential of the workflow if you eventually transition off the pension. Estimates only — not financial advice.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div>
          <label className={labelCls} htmlFor="dsp-fn-slider">Current DSP Fortnightly Income</label>
          <input id="dsp-fn-slider" data-testid="dsp-baseline-slider" type="range" min={800} max={1600} step={10} value={dspFn} onChange={(e) => setDspFn(parseInt(e.target.value, 10))} className="w-full accent-[#C74A00]" aria-valuetext={`${dspFn} dollars per fortnight`} />
          <p className="font-mono text-sm text-zinc-100 mt-2">{fmt(dspFn)} / fortnight</p>
        </div>
        <div>
          <label className={labelCls} htmlFor="dsp-sales-slider">Projected Asset Pack Sales / Month</label>
          <input id="dsp-sales-slider" data-testid="dsp-sales-slider" type="range" min={0} max={100} step={1} value={sales} onChange={(e) => setSales(parseInt(e.target.value, 10))} className="w-full accent-[#2E6BFF]" aria-valuetext={`${sales} sales per month`} />
          <p className="font-mono text-sm text-zinc-100 mt-2">{sales} sales</p>
        </div>
        <div>
          <label className={labelCls} htmlFor="dsp-price-slider">Price per Asset Pack</label>
          <input id="dsp-price-slider" data-testid="dsp-price-slider" type="range" min={10} max={150} step={5} value={price} onChange={(e) => setPrice(parseInt(e.target.value, 10))} className="w-full accent-[#2E6BFF]" aria-valuetext={`${price} dollars per pack`} />
          <p className="font-mono text-sm text-zinc-100 mt-2">{fmt(price)} / pack</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <div data-testid="dsp-baseline-result" className="border border-white/10 bg-[#050505]/60 p-5">
          <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Your Baseline DSP Revenue</p>
          <p className="font-sans font-bold text-2xl text-zinc-200 mt-2">{fmt(dspAnnual)} <span className="text-sm text-zinc-500 font-normal">/ year</span></p>
        </div>
        <div data-testid="dsp-creative-result" className="border border-nc-blue/40 bg-nc-blue/5 p-5">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">Your Specpixel Pro Creative Revenue</p>
          <p className="font-sans font-bold text-2xl text-zinc-100 mt-2">{fmt(creativeAnnual)} <span className="text-sm text-zinc-500 font-normal">/ year (80% creator split)</span></p>
        </div>
      </div>
      {diff > 0 && (
        <div data-testid="dsp-upside" role="status" className="border border-nc-green/40 bg-nc-green/10 p-5 mt-4">
          <p className="font-mono text-xs text-nc-green leading-relaxed">
            Take the workflow seriously: By scaling your asset catalog, your independent digital enterprise has the mathematical potential to outperform baseline DSP limits by <b>${diff.toLocaleString("en-AU")}</b> annually, paving a clear, self-directed path to financial independence.
          </p>
        </div>
      )}
    </div>
  );
}

export default function NdisPortalPage() {
  const [form, setForm] = useState(() => {
    try {
      return { ...DEFAULT_FORM, ...JSON.parse(localStorage.getItem(FORM_STORE) || "{}") };
    } catch { return DEFAULT_FORM; }
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      localStorage.setItem(FORM_STORE, JSON.stringify(next));
      return next;
    });
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const toggleProfile = (key) => {
    setForm((f) => {
      const has = f.accessibility_profiles.includes(key);
      const next = { ...f, accessibility_profiles: has ? f.accessibility_profiles.filter((x) => x !== key) : [...f.accessibility_profiles, key] };
      if (!next.accessibility_profiles.some((x) => x === "screen_reader" || x === "voice_control")) next.hardware_addon = false;
      localStorage.setItem(FORM_STORE, JSON.stringify(next));
      return next;
    });
  };

  const validate = () => {
    const e = {};
    if (form.participant_name.trim().length < 2) e.participant_name = "Please enter the participant's name.";
    const age = parseInt(form.participant_age, 10);
    if (!age || age < 16) e.participant_age = "Participants must be 16 or older to register.";
    const ndis = form.ndis_number.replace(/\s/g, "");
    if (!(ndis.length === 9 && /^\d+$/.test(ndis))) e.ndis_number = "Please check your NDIS number. It must be exactly 9 numbers with no letters.";
    if (form.contact_type !== "participant" && form.contact_name.trim().length < 2) e.contact_name = "Please enter your name so we know who to reply to.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email address, like name@example.com.";
    if (!form.track) e.track = "Please choose your program track — pick the one that fits you best. You can change it later with your coordinator.";
    if (form.track === "enterprise" && !/^\S+@\S+\.\S+$/.test(form.oversight_email)) e.oversight_email = "Please add your Plan Manager or Guardian's email so we can send them monthly earnings statements.";
    if (form.goal.trim().length < 10) e.goal = "Please tell us your goal in a sentence or two (at least 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("A few fields need a quick fix — they're marked below in plain language.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/ndis-onboarding/register", {
        ...form,
        participant_age: parseInt(form.participant_age, 10),
        revenue_cap_aud: parseFloat(form.revenue_cap_aud) || 400,
      });
      localStorage.removeItem(FORM_STORE);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (k) => errors[k] ? <p data-testid={`ndisreg-error-${k}`} role="alert" className="font-mono text-[11px] text-red-400 mt-1.5 leading-relaxed">{errors[k]}</p> : null;

  return (
    <div data-testid="ndis-portal-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <a href="#ndisRegistrationForm" className="skip-link">Skip to main form registration</a>
      <Seo
        title="NDIS Creator Portal — Register for the AI Game-Dev Pilot Program"
        description="Register for the Specpixel Advanced AI Game-Dev Pilot Program. Choose an educational or compliant micro-enterprise track, configure accessibility profiles, and sign your NDIS service agreement online."
        path="/ndis-portal"
        keywords="NDIS registration, disability game development program, DSP safe micro-enterprise, accessible AI tools, neurodivergent training program Australia"
      />
      <SiteNav />
      <main className="flex-1">
        <section className="border-b border-white/10">
          <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
            <p className="font-mono text-[10px] tracking-[0.35em] text-nc-orange uppercase mb-5">NDIS Creator Portal — Registration</p>
            <h1 data-testid="ndis-portal-title" className="font-sans font-bold text-3xl sm:text-4xl leading-tight tracking-tight">Register for the Advanced AI Game-Dev Pilot Program</h1>
            <p className="text-base text-zinc-400 leading-relaxed mt-4">
              One registration, everything set up: your program track, your accessibility profile, and your NDIS Service Agreement — sent to your inbox for a two-minute electronic signature. Your progress saves automatically on this device, so you can take breaks whenever you need.
            </p>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-6 py-12 flex flex-col gap-10">
          {done ? (
            <div data-testid="ndis-register-success" className="border border-nc-green/40 bg-nc-green/5 p-8 text-center">
              <CheckCircle2 className="w-8 h-8 text-nc-green mx-auto mb-4" />
              <p className="font-sans font-semibold text-lg text-zinc-100">Registration received — check your email.</p>
              <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-md mx-auto">
                Your NDIS Service Agreement has been sent to your inbox for electronic signature. It takes about two minutes. Once signed, we send the commencement statement to your Plan Manager automatically.
              </p>
            </div>
          ) : (
            <form id="ndisRegistrationForm" data-testid="ndis-register-form" onSubmit={submit} noValidate className="flex flex-col gap-10">
              {/* 1 — PERSONAL VERIFICATION */}
              <fieldset className="border border-white/10 bg-black/40 p-6 sm:p-8 flex flex-col gap-5">
                <legend className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase px-2">1. Personal Verification</legend>
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-5">
                  <div>
                    <label className={labelCls} htmlFor="ndisreg-name">Participant Name</label>
                    <input id="ndisreg-name" data-testid="ndisreg-name-input" className={inputCls} value={form.participant_name} onChange={(e) => set("participant_name", e.target.value)} aria-invalid={!!errors.participant_name} />
                    {fieldError("participant_name")}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ndisreg-age">Age (16+)</label>
                    <input id="ndisreg-age" data-testid="ndisreg-age-input" type="number" min={16} max={99} className={inputCls} value={form.participant_age} onChange={(e) => set("participant_age", e.target.value)} aria-invalid={!!errors.participant_age} />
                    {fieldError("participant_age")}
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="ndisreg-number">NDIS Number</label>
                  <input id="ndisreg-number" data-testid="ndisreg-number-input" inputMode="numeric" className={inputCls} placeholder="9 numbers, no letters" value={form.ndis_number} onChange={(e) => set("ndis_number", e.target.value)} aria-invalid={!!errors.ndis_number} />
                  {fieldError("ndis_number")}
                </div>
                <div>
                  <span className={labelCls}>Primary Contact</span>
                  <div className="flex flex-col sm:flex-row gap-2" role="radiogroup" aria-label="Primary contact">
                    {[["participant", "I am the participant"], ["parent_carer", "I am a parent, carer, or guardian"], ["coordinator", "I am a Support Coordinator"]].map(([v, label]) => (
                      <button key={v} type="button" data-testid={`ndisreg-contact-${v}`} onClick={() => set("contact_type", v)} aria-pressed={form.contact_type === v}
                        className={`flex-1 font-mono text-[11px] px-4 py-3 border transition-colors duration-300 text-left ${form.contact_type === v ? "border-nc-blue bg-nc-blue/10 text-zinc-100" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.contact_type !== "participant" && (
                  <div>
                    <label className={labelCls} htmlFor="ndisreg-contact-name">Contact Name</label>
                    <input id="ndisreg-contact-name" data-testid="ndisreg-contact-name-input" className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} aria-invalid={!!errors.contact_name} />
                    {fieldError("contact_name")}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="ndisreg-email">Contact Email</label>
                    <input id="ndisreg-email" data-testid="ndisreg-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                    {fieldError("email")}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ndisreg-phone">Contact Phone <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></label>
                    <input id="ndisreg-phone" data-testid="ndisreg-phone-input" type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="ndisreg-plan-type">NDIS Plan Management Type</label>
                  <select id="ndisreg-plan-type" data-testid="ndisreg-plan-type-select" className={inputCls} value={form.plan_type} onChange={(e) => set("plan_type", e.target.value)}>
                    <option value="plan_managed">Plan-Managed</option>
                    <option value="self_managed">Self-Managed</option>
                    <option value="agency_managed">Agency-Managed</option>
                    <option value="not_sure">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="ndisreg-goal">Primary Employment or Skill Goal</label>
                  <textarea id="ndisreg-goal" data-testid="ndisreg-goal-input" rows={3} className={`${inputCls} resize-y leading-relaxed`} placeholder="For example: I want to learn digital skills and work toward earning my own income online." value={form.goal} onChange={(e) => set("goal", e.target.value)} aria-invalid={!!errors.goal} />
                  {fieldError("goal")}
                </div>
              </fieldset>

              {/* 2 — PROGRAM TRACK */}
              <fieldset className="border border-white/10 bg-black/40 p-6 sm:p-8 flex flex-col gap-5">
                <legend className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase px-2">2. Select Your Program Track</legend>
                <div>
                  <label className={labelCls} htmlFor="ndisreg-track">Program Track (required)</label>
                  <select id="ndisreg-track" data-testid="ndisreg-track-select" className={inputCls} value={form.track} onChange={(e) => set("track", e.target.value)} aria-invalid={!!errors.track}>
                    <option value="">Choose your track…</option>
                    <option value="education">{TRACKS.education.label}</option>
                    <option value="enterprise">{TRACKS.enterprise.label}</option>
                  </select>
                  {fieldError("track")}
                </div>
                {form.track && (
                  <p data-testid="ndisreg-track-desc" className="font-mono text-xs text-zinc-400 leading-relaxed border border-white/10 bg-[#050505]/60 p-4">{TRACKS[form.track].desc}</p>
                )}
                {form.track === "enterprise" && (
                  <div data-testid="ndisreg-enterprise-fields" className="grid grid-cols-1 sm:grid-cols-2 gap-5 border-l-2 border-nc-orange/50 pl-5">
                    <div>
                      <label className={labelCls} htmlFor="ndisreg-cap">Nominated Monthly Revenue Cap ($AUD)</label>
                      <input id="ndisreg-cap" data-testid="ndisreg-cap-input" type="number" min={0} max={5000} step={10} className={inputCls} value={form.revenue_cap_aud} onChange={(e) => set("revenue_cap_aud", e.target.value)} />
                      <p className="font-mono text-[9px] text-zinc-600 mt-1.5">Defaults to $400 — safely under standard fortnightly DSP taper limits.</p>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="ndisreg-oversight">Plan Manager / Guardian Oversight Email</label>
                      <input id="ndisreg-oversight" data-testid="ndisreg-oversight-input" type="email" className={inputCls} placeholder="invoices@myplanmanager.com.au" value={form.oversight_email} onChange={(e) => set("oversight_email", e.target.value)} aria-invalid={!!errors.oversight_email} />
                      <p className="font-mono text-[9px] text-zinc-600 mt-1.5">Automated monthly earnings statements are routed here.</p>
                      {fieldError("oversight_email")}
                    </div>
                  </div>
                )}
                {form.track === "education" && (
                  <p data-testid="ndisreg-education-note" className="font-mono text-[10px] text-zinc-500 leading-relaxed flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-nc-green shrink-0 mt-0.5" /> Marketplace storefront creation is blocked on this track — all generated 3D assets stay locked to your private personal portfolio. Invoices map to NDIS code 09_011_0125_6_3 (Development of Daily Living and Life Skills).
                  </p>
                )}
              </fieldset>

              {/* DSP CALCULATOR — midway */}
              <DspCalculator />

              {/* 3 — VISUAL & SYSTEM ADJUSTMENTS */}
              <fieldset className="border border-white/10 bg-black/40 p-6 sm:p-8 flex flex-col gap-4">
                <legend className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase px-2 flex items-center gap-2"><Settings2 className="w-3.5 h-3.5" /> 3. Visual & System Adjustments <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></legend>
                <p className="font-mono text-[11px] text-zinc-500 leading-relaxed">Tick anything that helps. Your dashboard adapts automatically every time you log in.</p>
                {ACCESS_PROFILES.map(([key, label, desc]) => (
                  <label key={key} className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors ${form.accessibility_profiles.includes(key) ? "border-nc-blue/60 bg-nc-blue/5" : "border-white/10 hover:border-white/30"}`}>
                    <input type="checkbox" data-testid={`ndisreg-profile-${key}`} checked={form.accessibility_profiles.includes(key)} onChange={() => toggleProfile(key)} className="w-4 h-4 mt-0.5 accent-[#2E6BFF] shrink-0" />
                    <span>
                      <span className="font-sans font-semibold text-sm text-zinc-100 block">{label}</span>
                      <span className="font-mono text-[10px] text-zinc-500 block mt-1 leading-relaxed">{desc}</span>
                    </span>
                  </label>
                ))}

                {(form.accessibility_profiles.includes("screen_reader") || form.accessibility_profiles.includes("voice_control")) && (
                  <label data-testid="ndisreg-hardware-addon" className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors ${form.hardware_addon ? "border-nc-orange/60 bg-nc-orange/5" : "border-nc-orange/30 hover:border-nc-orange/60"}`}>
                    <input type="checkbox" data-testid="ndisreg-hardware-toggle" checked={form.hardware_addon} onChange={(e) => set("hardware_addon", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C74A00] shrink-0" />
                    <span>
                      <span className="font-sans font-semibold text-sm text-zinc-100 block flex items-center gap-2"><Wrench className="w-4 h-4 text-nc-orange" /> Request Premium 1:1 Technical Hardware Integration & Custom Calibration Support</span>
                      <span className="font-mono text-[10px] text-zinc-400 block mt-1 leading-relaxed">
                        Includes 1:1 direct technical setup sessions with a Specpixel engineer to calibrate our Image-to-3D AI software with your physical hardware (e.g., Tobii Dynavox eye-trackers, foot switches, or adaptive controllers). Includes quarterly system calibration reporting for your NDIS reviews.
                      </span>
                      <span className="font-mono text-[10px] text-nc-orange block mt-2">$193.99/hr specialist rate, capped at 4 hours ($775.96 maximum) — itemised separately under NDIS line item 15_056_0128_1_3.</span>
                    </span>
                  </label>
                )}
              </fieldset>

              {/* honeypot — invisible to humans */}
              <div className="relative h-0 overflow-hidden" aria-hidden="true">
                <input type="text" name="website" data-testid="ndisreg-honeypot" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" />
              </div>

              <button data-testid="ndisreg-submit" disabled={busy} type="submit" className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors w-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Register & Send My Service Agreement
              </button>
              <p className="font-mono text-[10px] text-zinc-600 leading-relaxed text-center">
                Your information is used only to deliver your program. We never share participant details. Read our <Link to="/marketplace-terms" className="text-nc-blue hover:underline">Marketplace & NDIS Compliance Terms</Link>.
              </p>
            </form>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
